<template>
    <transition name="dialog">
        <dialog
            v-if="matchSlugToActive"
            class="dialog"
            :data-name="content.slug"
            :style="inlineStyle"
        >
            <div
                class="dialog__background"
                @click.self="handleCloseDialog"
            ></div>

            <div class="dialog__inner">
                <div class="dialog__wrapper">
                    <div class="dialog__container container">
                        <div
                            v-if="!store.state.dialog.preventClose"
                            class="dialog__close"
                            @click.stop="handleCloseDialog"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                x="0px"
                                y="0px"
                                viewBox="0 0 16.2 16.2"
                                enable-background="new 0 0 16.2 16.2"
                                xml:space="preserve"
                            >
                                <polygon
                                    fill="#909090"
                                    points="16.2,2.4 13.8,0 8.1,5.7 2.4,0 0,2.4 5.7,8.1 0,13.8 2.4,16.2 8.1,10.5 13.8,16.2 16.2,13.8 10.5,8.1 "
                                />
                            </svg>
                        </div>

                        <zoe-atom-text
                            class="dialog__header"
                            :item="content.title"
                        />

                        <zoe-atom-text
                            class="dialog__content"
                            :item="content.content"
                        />

                        <zoe-molecule-lefty-progress
                            v-if="upsells.length || (store.state.page.isPreview && content.lefty_enabled)"
                            :progress-active-background="getValue('dialog.general.style.lefty.progressActiveBackground')"
                            :progress-background="getValue('dialog.general.style.lefty.progressBackground')"
                            :progress-border="getValue('dialog.general.style.lefty.progressBorder')"
                        />

                        <zoe-molecule-share-button
                            v-if="content.share_enabled"
                            :share-type="content.share_type"
                            :share-text="content.share_text"
                            :share-target="content.share_target"
                            :url="content.share_link"
                            :label="content.share_button"
                        />
                    </div>
                </div>
            </div>
        </dialog>
    </transition>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { componentProps } from '@mixins/component/component';
import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeMoleculeShareButton from '../../../molecules/share-button/zoe-molecule-share-button.vue';
import { generateButtonVariables, generateContainerVariables } from '@mixins/styling/styling';
import { getValue } from '../../../../../../resources/frontend/js/mixins/get-page-data';
import ZoeMoleculeLeftyProgress from '@molecules/lefty-progress/zoe-molecule-lefty-progress.vue';
import { useUpsellRedirect } from '@mixins/utils/upsell-redirect';

const store = useStore();
const props = defineProps({
    content: componentProps.content,
});

const url = props.content?.lefty_url;
const { handleUpsellRedirect, upsells } = useUpsellRedirect(url, 'dialog');

const matchSlugToActive = computed(() => {
    return props.content.slug === store.state.dialog.active;
});

watch(matchSlugToActive, newValue => {
    if (newValue) {
        window.addEventListener('keydown', onEscapeKeyDown);

        if (props.content.lefty_enabled) {
            handleUpsellRedirect();
        }
    } else {
        window.removeEventListener('keydown', onEscapeKeyDown);
    }
});

const inlineStyle = computed(() => {
    if (Object.keys(store.state.styling?.buttons).length === 0) return;

    return {
        ...generateButtonVariables(),
        ...generateContainerVariables(),
    };
});

const handleCloseDialog = event => {
    if (store.state.dialog.preventClose) return;

    store.commit('dialog/hide');
};

const onEscapeKeyDown = event => {
    if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        handleCloseDialog();
    }
};
</script>

<style lang="scss">
@import 'zoe-oragnism-dialog';

.dialog-enter-active,
.dialog-leave-active {
    transition: all .5s;

    .dialog__container {
        transition: all .5s;
    }
}

.dialog-enter-to,
.dialog-leave-from {
    opacity: 1;

    .dialog__container {
        transform: translate3d(0, 0, 0);
    }
}

.dialog-enter-from,
.dialog-leave-to {
    opacity: 0;

    .dialog__container {
        transform: translate3d(0, -40px, 0);
    }
}
</style>
